import { useMemo } from "react";
import { benchPoints, curseName, bountiesOn, cursesNow } from "../../../../engine/bench";
import { funBoard } from "../../../../engine/ranking";
import { currentRound } from "../../../../engine/selectors";
import { CourtBadge, Toggle, mmss, nameOf, pairNames, type TabProps } from "./common";

/** How many pickers the table shows. */
const PICKERS = 8;

/**
 * The bench as it needs an admin during the day: mute the heckles, switch curses off (which ends
 * any running and gives them back), lift a running curse.
 */
export function BenchNow({ t, state }: TabProps) {
  const { muted } = state.bench;
  const cursesOn = !state.bench.cursesOff;
  const round = currentRound(state);
  const curses = cursesNow(state, t.now());
  return (
    <section className="ad-card">
      <h2>The bench</h2>
      <Toggle label="Mute heckles" on={muted} onChange={(on) => t.send({ type: "bench.muted", on })} />
      <Toggle label="Curses" on={cursesOn} onChange={(on) => t.send({ type: "bench.curses", on })} />
      {curses.length ? (
        <ul className="ad-list">
          {curses.map((c) => {
            const game = round?.games.find((g) => g.court === c.court);
            return (
              <li key={c.court} className="ad-game ad-curse">
                <CourtBadge court={c.court} />
                <div className="ad-game-body">
                  <b>{nameOf(state, c.by)} cursed {game ? pairNames(state, game[c.side]) : `court ${c.court}`}</b>
                  <span>
                    {c.stepsOff && game ? `Solo, ${nameOf(state, game[c.side].find((id) => id !== c.stepsOff) ?? c.stepsOff)} plays alone` : curseName(c)},{" "}
                    {mmss(c.leftMs)} left
                  </span>
                </div>
                <button type="button" className="btn quiet" onClick={() => t.send({ type: "curse.lifted", court: c.court })}>
                  Lift
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="ad-note">{cursesOn ? "No pair is cursed right now." : "Curses are off. Nobody can throw one."}</p>
      )}
    </section>
  );
}

/** What the bench has been up to, inside its fold: the heckles (each can be removed), the round's bounties, how the picks stand. */
export function BenchFeed({ t, state }: TabProps) {
  const { muted, heckles } = state.bench;
  const round = currentRound(state);
  // best first: the fun points their picks have won, then the right ones, then the name
  const pickers = useMemo(
    () =>
      [...benchPoints(state)]
        .filter(([, b]) => b.right + b.wrong > 0)
        .map(([id, b]) => ({ id, name: nameOf(state, id), ...b }))
        .sort((x, y) => y.picks - x.picks || y.right - x.right || x.wrong - y.wrong || x.name.localeCompare(y.name, "en"))
        .slice(0, PICKERS),
    [state],
  );
  const priced = round
    ? round.games.flatMap((g) => {
        const on = bountiesOn(round, g.court);
        return [
          ...(on.a > 0 ? [{ court: g.court, pair: g.a, value: on.a }] : []),
          ...(on.b > 0 ? [{ court: g.court, pair: g.b, value: on.b }] : []),
        ];
      })
    : [];

  return (
    <>
      <h3>Heckles</h3>
      {heckles.length ? (
        <ul className="ad-list">
          {[...heckles].reverse().map((h) => (
            <li key={h.seq} className="ad-game">
              <div className="ad-game-body">
                <b>{nameOf(state, h.by)}, {h.court === null ? "at everyone" : `at court ${h.court}`}</b>
                <span>{h.text}</span>
              </div>
              <button type="button" className="ad-small" onClick={() => t.send({ type: "heckle.removed", heckleSeq: h.seq })}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="ad-note">{muted ? "Heckles are off." : "No heckles yet."}</p>
      )}

      <h3>Bounties{round ? `, round ${round.no}` : ""}</h3>
      {priced.length ? (
        <ul className="ad-list">
          {priced.map((b) => (
            <li key={`${b.court}${b.pair[0]}`} className="ad-game">
              <CourtBadge court={b.court} />
              <div className="ad-game-body">{pairNames(state, b.pair)}</div>
              <span className="ad-score">{b.value}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="ad-note">
          {round && round.no < state.config.bench.bountyFromRound
            ? `Bounties start in round ${state.config.bench.bountyFromRound}.`
            : "No bounty on court this round."}
        </p>
      )}

      <h3>Picks</h3>
      {pickers.length ? (
        <table className="ad-fun ad-picks">
          <thead>
            <tr>
              <th>Name</th>
              <th className="num">Right</th>
              <th className="num">Wrong</th>
              <th className="num">Run</th>
              <th className="num">Fun</th>
            </tr>
          </thead>
          <tbody>
            {pickers.map((p) => (
              <tr key={p.id}>
                <td>{p.name}</td>
                <td className="num">{p.right}</td>
                <td className="num">{p.wrong}</td>
                <td className="num">{p.streak}</td>
                <td className="num">{p.picks}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="ad-note">No pick has been settled yet.</p>
      )}
    </>
  );
}

/** Everyone's fun points, most first. */
export function FunPoints({ state }: TabProps) {
  const rows = useMemo(() => funBoard(state), [state]);
  return (
    <table className="ad-fun">
      <thead>
        <tr>
          <th>Name</th>
          <th className="num">Fun points</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td>{r.name}</td>
            <td className="num">{Math.round(r.funPoints * 100) / 100}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
